import { createRouter, createWebHistory } from "vue-router";
import { authGuard as _authGuard } from "@auth0/auth0-vue";
import { settingsRoutes } from "./settings";
import { beforeEnterFeatureFlagged } from "./middleware/beforeEnterFeatureFlagged";
import { tenantRoutes } from "./tenant";
import { assistantRoutes } from "./assistant";
import { scheduledRoutes } from "./scheduled";
import Bugsnag from "@bugsnag/js";
import { accessTokenMiddleware } from "./middleware/accessToken";
import { tenantMiddleware } from "./middleware/tenant";
import { emailRoutes } from "./email";
import { contentRoutes } from "./content";
import NProgress from "nprogress";

const authGuard = isDESKTOP ? undefined : _authGuard;
const isDev = import.meta.env.DEV;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/logout",
      name: RouteNames.LOGOUT,
      component: () => import("@/views/Logout.vue"),
      meta: {
        titleTag: "Logout | Marketing Assistant",
      },
    },
    // @ts-expect-error - ts having trouble with dynamic redirect
    {
      path: "/components",
      name: RouteNames.COMPONENTS,
      redirect: isDev ? undefined : "/home",
      component: () => import("@/views/ComponentsView.vue"),
      meta: {
        titleTag: "Components | Marketing Assistant",
      },
    },
    {
      path: "/init",
      name: RouteNames.INITIALIZE,
      beforeEnter: authGuard,
      component: () => import("@/views/Initialize.vue"),
      meta: {
        titleTag: "Init | Marketing Assistant",
        hideBanners: true,
        isFullScreen: true,
      },
    },
    {
      path: "/terms",
      name: RouteNames.TERMS,
      beforeEnter: authGuard,
      component: () => import("@/views/TermsAndConditions.vue"),
      meta: {
        titleTag: "Terms & Conditions | Marketing Assistant",
        hideBanners: true,
        isFullScreen: true,
      },
    },
    {
      path: "/login",
      name: RouteNames.LOGIN,
      component: () => import("@/views/Login.vue"),
      meta: {
        titleTag: "Login | Marketing Assistant",
      },
    },
    {
      path: "/plans",
      name: RouteNames.PLANS,
      beforeEnter: authGuard,
      component: () => import("@/views/SelectPlan.vue"),
      meta: {
        titleTag: "Plans | Marketing Assistant",
      },
    },
    {
      path: "/onboarding",
      name: RouteNames.ONBOARDING,
      beforeEnter: authGuard,
      component: () => import("@/views/OnboardingView.vue"),
      meta: {
        titleTag: "Onboarding | Marketing Assistant",
        hideFooter: true,
      },
    },
    {
      path: "/verify",
      name: RouteNames.VERIFY_ACCOUNT,
      beforeEnter: authGuard,
      component: () => import("@/views/VerifyAccount.vue"),
      meta: {
        titleTag: "Verify Account | Marketing Assistant",
      },
    },
    {
      path: "/payment-failed",
      name: RouteNames.PAYMENT_FAILED,
      beforeEnter: authGuard,
      component: () => import("@/views/PaymentFailed.vue"),
      meta: {
        titleTag: "Payment Failed | Marketing Assistant",
      },
    },
    {
      path: "/register",
      name: RouteNames.REGISTER,
      beforeEnter: authGuard,
      component: () => import("@/views/Register.vue"),
      meta: {
        titleTag: "Register | Marketing Assistant",
      },
    },
    /**
     * Tasks
     */
    {
      path: "/tasks",
      redirect: () => ({ name: RouteNames.TASK_LIST, params: { status: "recent" } }),
      beforeEnter: authGuard,
      children: [
        {
          path: ":status",
          name: RouteNames.TASK_LIST,
          component: () => import("../views/TaskList.vue"),
          meta: {
            titleTag: "Tasks List | Marketing Assistant",
          },
        },
      ],
    },

    /**
     * Task
     */
    {
      path: "/task",
      redirect: () => ({ name: RouteNames.TASK_LIST, params: { status: "recent" } }),
      beforeEnter: authGuard,
      children: [
        {
          path: "result/:id",
          name: RouteNames.SINGLE_TASK,
          beforeEnter: authGuard,
          component: () => import("../views/SingleTaskResult.vue"),
          meta: {
            titleTag: "Task Result | Marketing Assistant",
          },
        },
        {
          path: "create/:category/:type/:key",
          name: RouteNames.CREATE_TASK_NEW,
          beforeEnter: authGuard,
          component: () => import("../views/CreateTask.vue"),
        },

        {
          path: "search",
          name: RouteNames.SEARCH_TASKS,
          beforeEnter: authGuard,
          component: () => import("../views/NewTask.vue"),
          meta: {
            titleTag: "Create Task | Marketing Assistant",
          },
        },
        {
          path: "new",
          name: RouteNames.CREATE_TASK,
          component: () => import("../views/NewTask.vue"),
          meta: {
            titleTag: "Create Task | Marketing Assistant",
          },
          children: [
            {
              path: ":category",
              name: RouteNames.SINGLE_TASK_CATEGORY,
              component: () => import("../views/NewTask.vue"),
              meta: {
                titleTag: "Task Result | Marketing Assistant",
              },
              children: [
                {
                  path: ":type",
                  name: RouteNames.SINGLE_TASK_TYPE,
                  component: () => import("../views/NewTask.vue"),
                  meta: {
                    titleTag: "Task Result | Marketing Assistant",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "/emails/setup",
      name: RouteNames.EMAIL_SETUP,
      component: () => import("@/views/emails/EmailSetupView.vue"),
    },
    {
      path: "/emails",
      beforeEnter: isDESKTOP
        ? undefined
        : [beforeEnterFeatureFlagged("messagesUi"), _authGuard],
      component: () => import("../views/emails/EmailParentView.vue"),
      children: emailRoutes,
      meta: {
        titleTag: "Messages | Marketing Assistant",
        hideFooter: true,
        hideHeader: false,
        disableScrollToBottom: true,
        hideBanners: true,
      },
    },
    {
      path: "/settings",
      beforeEnter: authGuard,
      component: () => import("../views/SettingsView.vue"),
      children: settingsRoutes,
      meta: {
        titleTag: "Settings | Marketing Assistant",
      },
    },
    {
      path: "/tenant",
      beforeEnter: authGuard,
      children: tenantRoutes,
      meta: {
        titleTag: "Tenant | Marketing Assistant",
      },
    },
    {
      path: "/content",
      beforeEnter: authGuard,
      children: contentRoutes,
      meta: {
        titleTag: "Content | Marketing Assistant",
      },
    },
    {
      path: "/third-party",
      name: RouteNames.THIRD_PARTY,
      component: () => import("../views/ThirdParty.vue"),
      meta: {
        titleTag: "Third-party | Marketing Assistant",
      },
    },
    {
      path: "/restricted",
      name: RouteNames.RESTRICTED_VIEW,
      component: () => import("../views/RestrictedView.vue"),
      meta: {
        titleTag: "Restricted | Marketing Assistant",
      },
    },
    {
      path: "/maintenance",
      name: RouteNames.MAINTENANCE_VIEW,
      component: () => import("../views/MaintenanceView.vue"),
      meta: {
        titleTag: "Maintenance | Marketing Assistant",
      },
    },
    {
      path: "/my-assistant",
      beforeEnter: authGuard,
      children: assistantRoutes,
      meta: {
        titleTag: "My Assistant | Marketing Assistant",
      },
    },
    {
      path: "/welcome",
      name: RouteNames.WELCOME,
      beforeEnter: authGuard,
      component: () => import("../views/WelcomeView.vue"),
      meta: {
        titleTag: "Welcome | Marketing Assistant",
      },
    },
    {
      path: "/home",
      name: RouteNames.HOME,
      beforeEnter: authGuard,
      component: () => import("../views/HomeView.vue"),
      meta: {
        titleTag: "Home | Marketing Assistant",
      },
    },
    {
      path: "/new-releases",
      name: RouteNames.WHATSNEW,
      beforeEnter: authGuard,
      component: () => import("../views/WhatsNewView.vue"),
      meta: {
        titleTag: "What's New | Marketing Assistant",
      },
    },
    {
      path: "/scheduled",
      beforeEnter: authGuard,
      meta: {
        titleTag: "Scheduled Activities | Marketing Assistant",
      },
      children: scheduledRoutes,
    },
    {
      path: "/suggestions",
      name: RouteNames.SUGGESTIONS,
      beforeEnter: authGuard,
      component: () => import("../views/SuggestionsView.vue"),
      meta: {
        titleTag: "Suggestions | Marketing Assistant",
      },
    },
    {
      path: "/suggestions/:id",
      name: RouteNames.SINGLE_SUGGESTION,
      beforeEnter: authGuard,
      component: () => import("../views/SingleSuggestion.vue"),
      meta: {
        titleTag: "Suggestion Details | Marketing Assistant",
      },
    },
    {
      path: "/smart-tips",
      children: [
        {
          path: ":slug",
          name: RouteNames.SMART_TIPS_DETAILS,
          component: () => import("../views/SmartTipDetails.vue"),
          meta: {
            titleTag: "Smart Tip Details | Marketing Assistant",
          },
        },
        {
          path: "",
          name: RouteNames.SMART_TIPS,
          component: () => import("../views/SmartTipsView.vue"),
          meta: {
            titleTag: "Smart Tips | Marketing Assistant",
          },
        },
      ],
    },

    {
      path: "/",
      name: RouteNames.MAIN,
      beforeEnter: authGuard,
      redirect: "/init",
    },
    {
      path: "/error",
      name: RouteNames.ERROR,
      component: () => import("../views/ErrorView.vue"),
      meta: {
        titleTag: "Error | Marketing Assistant",
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: RouteNames.NOT_FOUND,
      component: () => import("../views/NotFound.vue"),
    },
  ],
});

router.onError((error, to) => {
  const isDynamicImportError = error.message.includes(
    "Failed to fetch dynamically imported module"
  );

  if (!isDynamicImportError) {
    Bugsnag.notify(error);
  }
  if (!to?.fullPath) {
    window.location.reload();
  } else {
    window.location.href = to.fullPath;
  }
});

router.beforeEach((to, from) => {
  if (to.name !== from.name && to.name !== RouteNames.INITIALIZE) {
    NProgress.start();
  }
});

router.afterEach(() => {
  NProgress.done();
});
router.beforeEach(accessTokenMiddleware);
router.beforeEach(tenantMiddleware);

export default router;
